import DOMPurify from "dompurify";

/* ── Bezpieczne renderowanie treści maili w zgłoszeniach (BRIEF-0017) ──
   Cel: czytelny, SFORMATOWANY HTML z wiadomości e-mail zamiast spłaszczonego
   tekstu — ale bez ryzyka XSS. Allowlista obejmuje wyłącznie tagi formatujące;
   skrypty, style, iframy i handlery zdarzeń (on*) są ZAWSZE wycinane. */

export const ALLOWED_TAGS = [
    "p", "br", "div", "span",
    "ul", "ol", "li",
    "table", "thead", "tbody", "tfoot", "tr", "td", "th",
    "strong", "b", "em", "i", "u", "s", "small", "sub", "sup",
    "a", "img",
    "h1", "h2", "h3", "h4", "h5", "h6",
    "blockquote", "pre", "code", "hr", "font",
];

const ALLOWED_ATTR = [
    // linki
    "href", "name", "target", "rel",
    // obrazy
    "src", "alt", "title", "width", "height",
    // tabele (typowe atrybuty maili z Outlooka)
    "colspan", "rowspan", "cellpadding", "cellspacing", "border",
    "align", "valign",
    // <font> — maile legacy
    "color", "face", "size",
    // formatowanie inline (DOMPurify nie przepuszcza JS przez style)
    "style",
];

const FORBID_TAGS = ["script", "style", "iframe", "object", "embed", "form", "input", "button", "link", "meta", "base"];

/* ── [A16-03] Filtr NIEBEZPIECZNYCH właściwości CSS w atrybucie style ──
   BYŁO: atrybut `style` przechodził w całości (DOMPurify wycina z niego JS, ale
   NIE wycina układu strony). Nadawca ZEWNĘTRZNY mógł przysłać maila/zaproszenie
   z `<div style="position:fixed;inset:0">` i zasłonić cały interfejs mPracownika
   własną treścią — np. podstawionym formularzem logowania (phishing bez JS,
   na zaufanym origin) albo pustą płachtą blokującą pracę.
   SKUTEK DLA UŻYTKOWNIKA: otwarcie zwykłego maila w wątku zgłoszenia (albo
   opisu zadania / zaproszenia w kalendarzu) mogło zasłonić aplikację.
   NAPRAWA: usuwamy WYŁĄCZNIE deklaracje pozwalające wyjść z ramy treści
   (position/offsety/z-index/transform/pointer-events/clip). Kolory, czcionki,
   ramki, marginesy, wyrównanie — czyli wszystko, czego realnie używają maile
   HTML i stopki — zostają nietknięte, więc wygląd normalnej poczty się nie zmienia. */
const UNSAFE_CSS_PROPS = [
    "position",
    "top", "right", "bottom", "left",
    "inset", "inset-block", "inset-block-start", "inset-block-end",
    "inset-inline", "inset-inline-start", "inset-inline-end",
    "z-index",
    "transform", "translate", "rotate", "scale",
    "pointer-events",
    "clip", "clip-path",
];
const UNSAFE_CSS_SET = new Set(UNSAFE_CSS_PROPS);

// Nazwę właściwości CSS wolno zapisać z ucieczkami (`\70 osition` = `position`).
// Bez rozkodowania filtr tekstowy dałby się obejść jednym backslashem.
const decodeCssIdent = (ident) =>
    ident
        .replace(/\\([0-9a-fA-F]{1,6})[ \t\n\r\f]?/g, (_m, hex) => {
            const code = parseInt(hex, 16);
            return code > 0 && code <= 0x10FFFF ? String.fromCodePoint(code) : "";
        })
        .replace(/\\(.)/g, "$1");

export const stripUnsafeCss = (node) => {
    if (!node || typeof node.getAttribute !== "function") return;
    const raw = node.getAttribute("style");
    if (!raw) return;

    // 1) Filtr na surowym atrybucie — deklaracja po deklaracji.
    //    Fragmenty bez dwukropka (np. ogon `;base64,...` z data: URI) zostawiamy
    //    nietknięte, żeby nie rozbić obrazków inline w stopkach maili.
    const kept = raw
        .split(";")
        .filter((decl) => {
            const idx = decl.indexOf(":");
            if (idx < 0) return true;
            const prop = decodeCssIdent(decl.slice(0, idx).replace(/\/\*[\s\S]*?\*\//g, ""))
                .trim()
                .toLowerCase();
            return !UNSAFE_CSS_SET.has(prop);
        })
        .join(";");
    if (kept !== raw) {
        if (kept.replace(/[;\s]/g, "")) node.setAttribute("style", kept);
        else node.removeAttribute("style");
    }

    // 2) Druga bariera: nazwy właściwości zapisane z ucieczkami CSS (`\70 osition`)
    //    przechodzą przez filtr tekstowy, ale parser przeglądarki je normalizuje —
    //    kasujemy je z JUŻ SPARSOWANEJ deklaracji (żywo powiązanej z atrybutem).
    const style = node.style;
    if (style && typeof style.removeProperty === "function") {
        UNSAFE_CSS_PROPS.forEach((prop) => {
            try {
                if (style.getPropertyValue(prop)) style.removeProperty(prop);
            } catch { /* właściwość nieobsługiwana przez silnik — nic do usunięcia */ }
        });
    }
};

/* ── [A16-07] Zdalne obrazy w treści od nadawców ZEWNĘTRZNYCH ──
   BYŁO ŹLE: `<img>` z adresem http(s) na obcy serwer ładował się sam, gdy tylko serwisant
   otworzył wiadomość w wątku zgłoszenia. To klasyczny „piksel śledzący": obcy serwer
   dostaje potwierdzenie, że mail został OTWARTY, dokładną godzinę otwarcia, publiczny
   adres IP firmy i nagłówek User-Agent przeglądarki pracownika. Ten sam wyciek szedł
   drugą drogą — przez `style="background-image:url(http://...)"`, bo filtr [A16-03]
   pilnuje układu strony, a nie pobierania zasobów.
   SKUTEK DLA UŻYTKOWNIKA: spamer albo nadawca phishingu dowiadywał się, że pisze pod
   żywy, obsługiwany adres i o której ktoś czyta jego wiadomości — bez żadnego kliknięcia.
   NAPRAWA: obrazy z OBCYCH adresów nie są pobierane automatycznie. Oryginalny adres
   odkładamy do `data-blocked-src`, a treść dostaje jednorazowy przycisk „Pokaż obrazy"
   (dokładnie ten sam mechanizm co w Outlooku i Gmailu). NIE dotykamy obrazów, które i tak
   nie wychodzą na zewnątrz: `cid:` (załączniki inline), `data:` (obrazki wklejone w treść)
   oraz adresy względne i z NASZEGO origin (załączniki serwowane przez API) — czyli
   zrzuty ekranu przysyłane przez pracowników wyglądają dokładnie tak, jak dotąd.
   ZMIANA ZACHOWANIA: w wątku zgłoszenia logotypy ze stopek maili zewnętrznych pokażą się
   dopiero po kliknięciu „Pokaż obrazy"; treść, układ i załączniki bez zmian. */

// Schematy, które NIE powodują żadnego zapytania na obcy serwer.
const SAFE_IMG_SCHEMES = /^(data:|cid:|blob:)/i;

const isRemoteUrl = (url) => {
    const value = String(url || "").trim();
    if (!value) return false;
    if (SAFE_IMG_SCHEMES.test(value)) return false;
    // Adres względny („/uploads/…", „obrazek.png") wskazuje na nasz własny serwer.
    try {
        const base = typeof window !== "undefined" && window.location ? window.location.href : "http://localhost/";
        const resolved = new URL(value, base);
        if (!/^https?:$/i.test(resolved.protocol)) return false;
        if (typeof window !== "undefined" && window.location && resolved.origin === window.location.origin) return false;
        return true;
    } catch {
        // Adresu nie da się zinterpretować — nic nie pobierze, nie ma czego blokować.
        return false;
    }
};

/* ── [N2 2026-09-13] SPRAWA KADROWA: WSZYSTKIE obrazy ukryte, dopóki człowiek nie kliknie ──
   Właściciel: „w itsm otwiera się to normalnie, natomiast w HR brakuje tutaj informacji w domyśle,
   że zdjęcia są ukryte, powinny być domyślnie ukryte”.
   ROOT CAUSE: HR i ITSM renderują wiadomość TYM SAMYM kodem (TicketDetailsModal), ale polityka
   [A16-07] wyżej chroni przed PIKSELEM ŚLEDZĄCYM, więc ukrywa wyłącznie obrazy z OBCEGO serwera.
   Obrazy w sprawach kadrowych nie są obce: poczta przychodząca niesie je jako załączniki inline
   `cid:`, które `EmailTicketService` przepisuje na ścieżkę WZGLĘDNĄ naszego serwera (`publicPath`),
   a treść z formularza portalu ma obrazki wklejone jako `data:image` (HtmlContentSanitizer świadomie
   je zostawia). Dla `isRemoteUrl` oba rodzaje są „bezpieczne” — nic nie zostaje zablokowane, więc
   `didBlockRemoteImages()` zwraca false i pasek „Pokaż obrazy” się NIE rysuje. Zgłoszenie IT z logo
   w stopce zewnętrznego nadawcy pasek ma — stąd różnica widziana przez właściciela.
   NAPRAWA: tryb `blockAllImages` ukrywa KAŻDY obraz niezależnie od adresu (także data:, cid:, blob:,
   adres względny i nasz origin — czyli skany i zdjęcia dokumentów) oraz każdą funkcję obrazu w stylu
   (url, image-set, cross-fade, image). Adres NIE zostaje w `src`, więc przeglądarka niczego nie
   pobiera; w miejscu obrazu stoi tekst alternatywny. Bez tej opcji zachowanie jest DOKŁADNIE takie
   jak dotąd (ITSM). */
let blockAllImagesInCurrentCall = false;

// Czy ten adres obrazu ukryć w BIEŻĄCYM wywołaniu: w sprawie kadrowej każdy niepusty, poza nią — tylko obcy.
const shouldHideImageUrl = (url) =>
    blockAllImagesInCurrentCall ? String(url || "").trim() !== "" : isRemoteUrl(url);

// Funkcje CSS, którymi styl potrafi pobrać obraz — także BEZ url(...), np. image-set("skan.png" 1x).
const CSS_IMAGE_FUNCTION = /(?:url|image-set|cross-fade|image)\s*\(/i;

/* [N2] Deklaracje stylu rozdzielone średnikiem stojącym POZA cudzysłowem i nawiasem. Zwykłe split(";")
   cięło `image-set("data:image/png;base64,…" 1x)` w pół: pierwsza połowa wypadała, a w atrybucie
   zostawał ogon `base64,…" 1x)` — nic nie pobierał, ale był śmieciem w treści. Niedomknięty cudzysłów
   albo nawias skleja resztę w JEDNĄ deklarację, więc filtr może wyciąć więcej, nigdy mniej. */
const rozbijDeklaracjeCss = (raw) => {
    const deklaracje = [];
    let biezaca = "";
    let cudzyslow = null;
    let glebokosc = 0;
    for (let i = 0; i < raw.length; i += 1) {
        const znak = raw[i];
        if (cudzyslow) {
            biezaca += znak;
            if (znak === "\\" && i + 1 < raw.length) { biezaca += raw[i + 1]; i += 1; }
            else if (znak === cudzyslow) cudzyslow = null;
            continue;
        }
        if (znak === "\"" || znak === "'") cudzyslow = znak;
        else if (znak === "(") glebokosc += 1;
        else if (znak === ")" && glebokosc > 0) glebokosc -= 1;
        else if (znak === ";" && glebokosc === 0) { deklaracje.push(biezaca); biezaca = ""; continue; }
        biezaca += znak;
    }
    deklaracje.push(biezaca);
    return deklaracje;
};

/* [N2] Odstępnik / piksel 1×1 (typowe w stopkach Outlooka). Po ukryciu nie ma czego pokazywać, a obramowany
   zastępnik z napisem „[obraz ukryty]” zamieniałby każdy taki piksel w widoczną ramkę w treści. */
const WYMIAR_ODSTEPNIKA = /^\s*[0-2](?:\.\d+)?\s*(?:px)?\s*$/i;
const jestOdstepnikiem = (node) => {
    const style = node.style || {};
    return [node.getAttribute("width"), node.getAttribute("height"), style.width, style.height]
        .some((v) => v != null && v !== "" && WYMIAR_ODSTEPNIKA.test(String(v)));
};

const stripAllCssImages = (node) => {
    if (!node || typeof node.getAttribute !== "function") return false;
    let blocked = false;
    const raw = node.getAttribute("style");
    if (raw) {
        // Nazwę funkcji wolno zapisać z ucieczką CSS (`\75 rl(`) — badamy postać rozkodowaną.
        const kept = rozbijDeklaracjeCss(raw)
            .filter((decl) => {
                if (!CSS_IMAGE_FUNCTION.test(decodeCssIdent(decl.replace(/\/\*[\s\S]*?\*\//g, "")))) return true;
                blocked = true;
                return false;
            })
            .join(";");
        if (blocked) {
            if (kept.replace(/[;\s]/g, "")) node.setAttribute("style", kept);
            else node.removeAttribute("style");
        }
    }
    // Druga bariera jak w [A16-03]: deklaracje już sparsowane przez silnik przeglądarki.
    const style = node.style;
    if (style && typeof style.removeProperty === "function" && typeof style.length === "number") {
        for (let i = style.length - 1; i >= 0; i -= 1) {
            const prop = style[i];
            try {
                if (prop && CSS_IMAGE_FUNCTION.test(style.getPropertyValue(prop))) {
                    style.removeProperty(prop);
                    blocked = true;
                }
            } catch { /* właściwość nieobsługiwana przez silnik — nic do usunięcia */ }
        }
    }
    return blocked;
};

// Wycina z deklaracji CSS odwołania url(...) do obcych serwerów (background-image i pokrewne).
const CSS_URL = /url\(\s*(['"]?)([^'")]+)\1\s*\)/gi;
const stripRemoteCssUrls = (node) => {
    if (!node || typeof node.getAttribute !== "function") return false;
    const raw = node.getAttribute("style");
    if (!raw || raw.toLowerCase().indexOf("url(") < 0) return false;

    let blocked = false;
    const cleaned = raw.replace(CSS_URL, (match, _q, url) => {
        if (!shouldHideImageUrl(url)) return match;
        blocked = true;
        return "none";
    });
    if (blocked) {
        if (cleaned.replace(/[;\s]/g, "")) node.setAttribute("style", cleaned);
        else node.removeAttribute("style");
    }
    return blocked;
};

// Znaczniki wstawiane przez NAS. Wchodzącą treść czyścimy z nich zawsze, żeby nadawca
// nie mógł podstawić własnego „ukrytego" adresu, który odsłoni się po kliknięciu przycisku.
const BLOCKED_SRC_ATTR = "data-blocked-src";
const BLOCKED_FLAG_ATTR = "data-blocked-remote-image";
/* [N2] Znacznik obrazu ukrytego ze względu na PRYWATNOŚĆ (sprawa kadrowa) — po nim arkusze karty
   rysują w miejscu obrazu obramowany zastępnik z tekstem alternatywnym. */
export const BLOCKED_PRIVACY_ATTR = "data-blocked-privacy";
export const HIDDEN_IMAGE_ALT = "[obraz ukryty]";
/* [N2] Wartość znacznika dla odstępnika / piksela ≤ 2 px — arkusze go nie rysują (nie ma czego pokazać). */
export const BLOCKED_PRIVACY_SPACER = "maly";

/* DOMPurify kasuje atrybuty `data-*` (ALLOW_DATA_ATTR:false) ZANIM zdąży się wykonać hook
   `afterSanitizeAttributes`. Gdyby nie ten zapis, powtórna sanityzacja już-zablokowanego
   obrazu gubiłaby odłożony adres i przycisk „Pokaż obrazy" nie miałby czego przywrócić
   (ten util ma zagwarantowaną idempotencję — pilnuje jej osobny test). Przepisujemy adres
   TYLKO wtedy, gdy element nie ma `src`, czyli gdy to nasz własny, wcześniej zablokowany
   obraz — a nie znacznik podstawiony przez nadawcę obok działającego `src`. */
const CARRIED_BLOCKED_SRC = "__a1607CarriedBlockedSrc";

const rememberBlockedSrc = (node) => {
    if (!node || node.tagName !== "IMG" || typeof node.getAttribute !== "function") return;
    const previous = node.getAttribute(BLOCKED_SRC_ATTR);
    node[CARRIED_BLOCKED_SRC] = (previous && !node.getAttribute("src")) ? previous : null;
};

const blockRemoteImages = (node) => {
    if (!node || typeof node.getAttribute !== "function") return false;

    if (typeof node.removeAttribute === "function") {
        node.removeAttribute(BLOCKED_SRC_ATTR);
        node.removeAttribute(BLOCKED_FLAG_ATTR);
        node.removeAttribute(BLOCKED_PRIVACY_ATTR);
    }

    let blocked = stripRemoteCssUrls(node);
    if (blockAllImagesInCurrentCall && stripAllCssImages(node)) blocked = true;

    if (node.tagName === "IMG") {
        const src = node.getAttribute("src");
        const carried = node[CARRIED_BLOCKED_SRC];
        const hidden = shouldHideImageUrl(src) ? src : (!src && shouldHideImageUrl(carried) ? carried : null);
        if (hidden) {
            const odstepnik = blockAllImagesInCurrentCall && jestOdstepnikiem(node);
            // `alt` PRZED znacznikami `data-*`: powtórna sanityzacja daje wtedy identyczny napis (idempotencja).
            if (blockAllImagesInCurrentCall && !odstepnik && !String(node.getAttribute("alt") || "").trim()) {
                node.setAttribute("alt", HIDDEN_IMAGE_ALT);
            }
            node.setAttribute(BLOCKED_SRC_ATTR, hidden);
            node.setAttribute(BLOCKED_FLAG_ATTR, "1");
            if (blockAllImagesInCurrentCall) node.setAttribute(BLOCKED_PRIVACY_ATTR, odstepnik ? BLOCKED_PRIVACY_SPACER : "1");
            node.removeAttribute("src");
            blocked = true;
        }
    }
    return blocked;
};

/* Flaga „w tym wywołaniu wolno zdalne obrazy". DOMPurify.sanitize jest w pełni
   SYNCHRONICZNY, więc pojedyncza zmienna modułowa jest bezpieczna — między jej
   ustawieniem a zakończeniem sanityzacji nic innego się nie wykona. */
let allowRemoteImagesInCurrentCall = false;
// Czy ostatnia sanityzacja coś zablokowała (do pokazania paska „Pokaż obrazy").
let lastCallBlockedRemote = false;

const applyRemoteImagePolicy = (node) => {
    if (allowRemoteImagesInCurrentCall) return;
    if (blockRemoteImages(node)) lastCallBlockedRemote = true;
};

/**
 * Czy w OSTATNIO sanityzowanym HTML-u zablokowano zdalne obrazy.
 * Wywoływać bezpośrednio po sanitizeHtml/sanitizeRichText (obie funkcje są synchroniczne).
 * @returns {boolean}
 */
export const didBlockRemoteImages = () => lastCallBlockedRemote;

// DEDYKOWANA instancja DOMPurify (factory) — hook linków dotyczy TYLKO tego
// utila, a nie globalnej instancji współdzielonej z innymi modułami aplikacji
// (e-learning, baza wiedzy itd. używają własnego DOMPurify.sanitize).
const purifier = typeof window !== "undefined" ? DOMPurify(window) : DOMPurify;

// Każdy link z maila otwiera się w nowej karcie, bez dostępu do
// window.opener (tabnabbing).
purifier.addHook("beforeSanitizeAttributes", rememberBlockedSrc);
purifier.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName === "A") {
        node.setAttribute("target", "_blank");
        node.setAttribute("rel", "noopener noreferrer");
    }
    stripUnsafeCss(node);
    applyRemoteImagePolicy(node);
});

/**
 * Sanityzuje HTML wiadomości e-mail do bezpiecznego, sformatowanego renderu.
 * @param {string} html surowy HTML (np. TicketMessage.BodyHtml)
 * @param {{allowRemoteImages?: boolean}} [options] `allowRemoteImages` — pobierz obrazy
 *        z obcych serwerów (świadoma zgoda użytkownika: przycisk „Pokaż obrazy",
 *        albo treść tworzona przez administratora, gdzie nie ma nadawcy zewnętrznego).
 *        Domyślnie `false` — [A16-07].
 *        `blockAllImages` — [N2] sprawa kadrowa: ukryj KAŻDY obraz (data:, cid:, blob:, adres
 *        względny, nasz origin, obcy serwer) i każdą funkcję obrazu w stylu. Zgoda
 *        `allowRemoteImages: true` odsłania wszystko. `didBlockRemoteImages()` mówi wtedy, czy
 *        cokolwiek ukryto. Domyślnie `false` (ITSM bez zmian).
 * @returns {string} bezpieczny HTML do dangerouslySetInnerHTML
 */
export const sanitizeHtml = (html, options) => {
    if (!html) { lastCallBlockedRemote = false; return ""; }
    allowRemoteImagesInCurrentCall = options?.allowRemoteImages === true;
    blockAllImagesInCurrentCall = options?.blockAllImages === true;
    lastCallBlockedRemote = false;
    try {
        return purifier.sanitize(String(html), {
            ALLOWED_TAGS,
            ALLOWED_ATTR,
            FORBID_TAGS,
            // on* i tak nie przechodzi przez ALLOWED_ATTR — jawnie dla czytelności:
            FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus"],
            ALLOW_DATA_ATTR: false,
        });
    } finally {
        allowRemoteImagesInCurrentCall = false;
        blockAllImagesInCurrentCall = false;
    }
};

/* ── [A16-03] Sanityzacja treści od NIE-ADMINÓW i nadawców ZEWNĘTRZNYCH ──
   BYŁO: opis zadania Kanban (KanbanTaskModal) i treść zaproszenia z Outlooka
   (CalendarEventDetailPanel) leciały przez `DOMPurify.sanitize(x)` BEZ opcji.
   Domyślna konfiguracja przepuszcza m.in. <form>, <input type=password>,
   <button>, <svg>, <math> oraz dowolny `style` — zweryfikowane na dompurify 3.3.1.
   SKUTEK DLA UŻYTKOWNIKA: dowolny zalogowany (opis karty) albo NADAWCA
   ZEWNĘTRZNY (body zaproszenia) mógł wyświetlić innym pracownikom fałszywy
   formularz logowania / płachtę zasłaniającą aplikację na zaufanym origin.
   NAPRAWA: ta sama twarda allowlista tagów formatujących co dla maili
   (żadnych form/input/button/svg/math) + filtr niebezpiecznego CSS.
   Zachowane zostaje wszystko, czego używa edytor Quill i poczta HTML:
   pogrubienia, listy, linki, obrazki, tabele, kolory. */

export const RICH_ALLOWED_ATTR = [
    ...ALLOWED_ATTR,
    // klasy edytora Quill (`ql-*`) — bez nich listy/wcięcia z opisów kart
    // straciłyby formatowanie; filtr klas poniżej przepuszcza tylko ten prefiks.
    "class",
    // Quill 2 zapisuje rodzaj listy jako data-list="bullet|ordered"
    "data-list",
];

export const RICH_FORBID_TAGS = [
    ...FORBID_TAGS,
    "select", "textarea", "option", "label", "fieldset", "legend",
    "svg", "math", "template", "dialog",
];

const SAFE_CLASS_PREFIXES = ["ql-"];

const richPurifier = typeof window !== "undefined" ? DOMPurify(window) : DOMPurify;

richPurifier.addHook("beforeSanitizeAttributes", rememberBlockedSrc);
richPurifier.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName === "A") {
        node.setAttribute("target", "_blank");
        node.setAttribute("rel", "noopener noreferrer");
    }
    stripUnsafeCss(node);
    applyRemoteImagePolicy(node);

    // Klasy spoza edytora są zbędne (arkusze <style> z maili i tak wycinamy),
    // a mogłyby wskazać na globalną klasę aplikacji przesuwającą element po ekranie.
    if (typeof node.getAttribute === "function") {
        const cls = node.getAttribute("class");
        if (cls) {
            const keptClasses = cls
                .split(/\s+/)
                .filter((c) => c && SAFE_CLASS_PREFIXES.some((p) => c.startsWith(p)));
            if (keptClasses.length) node.setAttribute("class", keptClasses.join(" "));
            else node.removeAttribute("class");
        }
    }
});

/**
 * Sanityzuje treść tworzoną przez zwykłych użytkowników i nadawców zewnętrznych
 * (opis zadania Kanban, body zaproszenia z Outlooka).
 *
 * [A16-07] Domyślnie ZOSTAWIA zdalne obrazy — inaczej niż `sanitizeHtml`. Ta funkcja
 * renderuje przede wszystkim treść z NASZEGO edytora (opisy kart Kanban), gdzie pracownik
 * może świadomie wkleić adres obrazka; zablokowanie go bez przycisku „Pokaż obrazy" byłoby
 * dla niego regresją („zniknął mi obrazek z opisu"). Blokada zdalnych obrazów jest tu
 * dostępna przez `allowRemoteImages: false` i czeka na osobne wpięcie paska zgody
 * w KanbanTaskModal / CalendarEventDetailPanel.
 *
 * @param {string} html surowy HTML
 * @param {{allowRemoteImages?: boolean}} [options] `allowRemoteImages` — domyślnie `true`.
 * @returns {string} bezpieczny HTML do dangerouslySetInnerHTML
 */
export const sanitizeRichText = (html, options) => {
    if (!html) { lastCallBlockedRemote = false; return ""; }
    allowRemoteImagesInCurrentCall = options?.allowRemoteImages !== false;
    lastCallBlockedRemote = false;
    try {
        return richPurifier.sanitize(String(html), {
            ALLOWED_TAGS,
            ALLOWED_ATTR: RICH_ALLOWED_ATTR,
            FORBID_TAGS: RICH_FORBID_TAGS,
            FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus"],
            ALLOW_DATA_ATTR: false,
        });
    } finally {
        allowRemoteImagesInCurrentCall = false;
    }
};

/**
 * Zamienia czysty tekst (notatki, maile bez HTML) na bezpieczny HTML:
 * escapuje &, <, >, " i tłumaczy nowe linie na <br />.
 * @param {string} text czysty tekst
 * @returns {string} bezpieczny HTML
 */
export const plainTextToSafeHtml = (text) => {
    if (!text) return "";
    return String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/\r?\n/g, "<br />");
};

const sanitizeHtmlUtils = { sanitizeHtml, sanitizeRichText, plainTextToSafeHtml, didBlockRemoteImages };
export default sanitizeHtmlUtils;
